import React from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

const PALETTE = ['#00327d', '#006a6a', '#ba1a1a', '#6f5675', '#556500', '#9c4238', '#00658f']

/**
 * columns (optional): the selected_meta / preview columns array, used only to
 * resolve friendly labels for multi-metric legends. Safe to omit — falls back
 * to the raw field key.
 */
export default function ReportChartRenderer({ visualization, chartData = [], summaryCards = [], columns = [] }) {
  if (!visualization || visualization.type === 'TABLE') return null

  const seriesKeys =
    Array.isArray(visualization.series) && visualization.series.length > 1 ? visualization.series : null

  function labelFor(key) {
    const col = columns.find((c) => c.key === key)
    return col?.label || key
  }

  if (visualization.type === 'KPI') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-surface-container-low rounded-[10px] border border-surface-container mb-6">
        {summaryCards.map((card) => (
          <div key={card.key} className="bg-surface p-4 rounded-[8px] border border-surface-container-high">
            <span className="text-[10px] font-extrabold uppercase text-on-surface-variant block tracking-wider">
              {card.label}
            </span>
            <div className="text-xl font-headline font-extrabold text-primary mt-1">
              {card.format === 'currency' ? `₹${card.value.toLocaleString()}` : card.value}
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="p-8 text-center bg-surface-container-low rounded-[10px] border border-surface-container mb-6 text-xs text-on-surface-variant">
        Not enough aggregated data to render chart preview. Select valid X/Y axes and ensure filters return records.
      </div>
    )
  }

  return (
    <div className="p-4 bg-surface-container-low rounded-[10px] border border-surface-container mb-6 space-y-2">
      {visualization.title && (
        <h4 className="text-sm font-headline font-bold text-on-surface">{visualization.title}</h4>
      )}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {visualization.type === 'BAR' || visualization.type === 'CHART_TABLE' ? (
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis dataKey="x" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              {seriesKeys ? (
                <>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {seriesKeys.map((key, i) => (
                    <Bar key={key} dataKey={key} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} name={labelFor(key)} />
                  ))}
                </>
              ) : (
                <Bar dataKey="y" fill="#00327d" radius={[4, 4, 0, 0]} name={visualization.yAxis ? labelFor(visualization.yAxis) : 'Metric'} />
              )}
            </BarChart>
          ) : visualization.type === 'LINE' ? (
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
              <XAxis dataKey="x" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              {seriesKeys ? (
                <>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {seriesKeys.map((key, i) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={PALETTE[i % PALETTE.length]}
                      strokeWidth={2.5}
                      dot={{ r: 3 }}
                      name={labelFor(key)}
                    />
                  ))}
                </>
              ) : (
                <Line
                  type="monotone"
                  dataKey="y"
                  stroke="#00327d"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  name={visualization.yAxis ? labelFor(visualization.yAxis) : 'Metric'}
                />
              )}
            </LineChart>
          ) : visualization.type === 'PIE' ? (
            <PieChart>
              <Tooltip />
              <Legend />
              <Pie
                data={chartData}
                dataKey="y"
                nameKey="x"
                cx="50%"
                cy="50%"
                outerRadius={80}
                fill="#00327d"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={PALETTE[index % PALETTE.length]} />
                ))}
              </Pie>
            </PieChart>
          ) : null}
        </ResponsiveContainer>
      </div>
    </div>
  )
}
